'use client';

import { ChevronDown, ChevronUp } from '@ps/web-fusion-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useContext } from 'react';
import type { Key, SelectProps as ReactAriaSelectProps } from 'react-aria-components';
import {
  Button,
  Label,
  ListBox,
  Popover,
  Select as ReactAriaSelect,
  SelectStateContext,
  SelectValue
} from 'react-aria-components';

import Icon from '../Icon/Icon';
import { useFusionTheme } from '../hooks';

import OptionItem from './Option';
import styles from './Select.module.scss';

interface Option<T extends Key> {
  id: T;
  name: string;
  [key: string]: unknown;
}

export type SelectProps<K extends Key = Key> = Omit<
  ReactAriaSelectProps<Option<K>>,
  'children' | 'validate' | 'validationBehavior' | 'onChange'
> & {
  label?: string;
  onChange?: (key: K) => void;
  children?: React.ReactNode;
  options?: Option<K>[];
} & (
    | { options?: never; children: React.ReactNode }
    | { options: Option<K>[]; children?: React.ReactNode }
  ) &
  VariantProps<typeof selectVariants>;

function SelectButton() {
  const state = useContext(SelectStateContext);
  return (
    <Button className={styles.button}>
      <SelectValue className={styles.selectValue} />
      <Icon className={styles.icon} icon={state?.isOpen ? ChevronUp : ChevronDown} aria-hidden />
    </Button>
  );
}

const selectVariants = cva(styles.container, {
  variants: {
    size: {
      default: '',
      small: styles.sizeSmall
    }
  }
});

export function Select<T extends Key = Key>({
  label,
  children,
  options,
  size,
  className,
  onChange,
  ...props
}: SelectProps<T>) {
  const { addThemeClassName } = useFusionTheme();

  const content = children
    ? children
    : options?.map((option) => (
        <OptionItem key={option.id} id={option.id}>
          {option.name}
        </OptionItem>
      ));

  return (
    <ReactAriaSelect<Option<T>>
      {...props}
      onChange={(key: Key | null) => {
        if (onChange) onChange(key as T);
      }}
      className={addThemeClassName(selectVariants({ size }), className)}
    >
      {label && <Label className={styles.label}>{label}</Label>}
      <SelectButton />
      <Popover className={addThemeClassName(selectVariants({ size }), styles.popover)} offset={0}>
        <ListBox<Option<T>> selectionMode="single" className={styles.dropdownMenu}>
          {content}
        </ListBox>
      </Popover>
    </ReactAriaSelect>
  );
}

export default Select;
